// @flow
import React, { Component, Fragment } from 'react'
import Button from '../controls/PrimaryButton'
import type { Configs, Crop, GroupedOptionType, OptionType, UrlCheckResult } from '../types'
import styled, { type StyledComponent } from 'styled-components'
import _ from 'lodash'
import PreviewImageCropperContainer from '../AdEdit/PreviewImageCropperContainer'
import FloatPanel from '../controls/FloatPanel'

import type {
  State as StoreState,
  AdvertisementTemplateAudienceData
} from '../../redux/ducks/advertisementTemplate/reducers'
import scrollToTop from '../../shared/scrollToTop'
import ErrorText from '../controls/ErrorText'
import LinkButtonsContainer from '../AdEdit/AdInputs/LinkButtonsContainer'
import LineInput from '../AdEdit/AdInputs/LineInput'
import type { RawAdvertisementTemplate } from '../../redux/ducks/AdvertisementDataAdapter'
import AudiencesSelectorContainer from './AudiencesSelectorContainer'
import PreviewsContainer from './PreviewsContainer'
import AdInputs from '../AdEdit/AdInputs'
import AdditionalImageLoader from '../AdEdit/AdditionalImageLoader'
import ReplicatedDataContainer from './EditForm/ReplicatedDataContainer'
import { Media, Row, type ControlVisibility } from '../AdEdit'
import CarouselContainer from '../AdEdit/CarouselContainer'

const t = {
  name: 'Название шаблона',
  create: 'Создать',
  update: 'Обновить',
  copy: 'Копировать',
  logo_crop: 'Логотип',
  form_has_errors: 'Форма заполнена с ошибками',
  cancel: 'Отменить',
  ad_in_audience: 'Объявление в аудитории',
  will_be_detached: 'будет отвязано от шаблона и остановлено',
  video: 'Видео',
  preview: 'Предпросмотр',
  add_media: 'Добавить объявление',
  logo_uploader_text:
    'Загрузка логотипа: по умолчанию мы используем логотип-заглушку, но вы можете ',
  snippet_uploader_text: 'Загрузка сниппета: ',
  snippet_crop: 'Сниппет для ВКонтакте',
  choose_logo_image: 'загрузить свой логотип',
  choose_snippet_image: 'загрузить сниппет',
  submit_inactive: 'Создать остановленными'
}

const FormError: StyledComponent<Object, any, any> = styled.div`
  clear: both;
  margin: 20px 0;
  color: red;
  font-weight: bold;
`
export const Header: StyledComponent<Object, any, any> = styled.div`
  font-size: 14px;
  vertical-align: top;
  padding-bottom: 4px;
`

const Wrapper: StyledComponent<Object, any, any> = styled.div``
const RemovedAudienceComment: StyledComponent<Object, any, any> = styled.div`
  font-size: 14px;
`
const RemovedAudiencesWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 8px;

  ${RemovedAudienceComment}:not(:last-of-type) {
    padding-bottom: 6px;
  }
`
const RemovedAudienceName: StyledComponent<Object, any, any> = styled.span`
  font-weight: bold;
`
const PreviewHeader: StyledComponent<Object, any, any> = styled.h1`
  padding: 20px 0 30px;
`
const PaddedWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 10px;
`
const AdditionalUploaderText: StyledComponent<Object, any, any> = styled.span`
  font-size: 16px;
`
const ButtonsWrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -5px;
`
const StyledButton: StyledComponent<Object, any, any> = styled(Button)`
  margin-left: 5px;
`

export type Initial = {|
  id: ?number,
  audiences: Array<AdvertisementTemplateAudienceData>,
  template_with_relations: RawAdvertisementTemplate,
  audience_options: Array<GroupedOptionType>,
  edit_form_copy_path: string
|}

type State = {|
  areChangesPersisted: boolean
|}

export type Props = {|
  action: 'copy' | 'update' | 'create',
  allowMultiUpload: boolean,
  audiences: Array<AdvertisementTemplateAudienceData>,
  configs: Configs,
  controlVisibility: ControlVisibility,
  data: StoreState,
  hideAudiences: boolean,
  initialSelectedAudienceIds: Array<number>,
  isAnyErrors: boolean,
  isLoading: boolean,
  onAudienceIdsChange: (Array<number>) => void,
  onColumnDescriptionChange: (string) => void,
  onVkTitleChange: (string) => void,
  onFbTitleChange: (string) => void,
  onFbLinkButtonChange: (string) => void,
  onFbMultiDescriptionChange: (string) => void,
  onFbUrlChange: (string) => void,
  onLogoImageChange: (string) => void,
  onLogoImageCropChange: (Crop) => void,
  onMediaAdd: () => void,
  onMultiDescriptionChange: (string) => void,
  onNameChange: (string) => void,
  onPersist: (string, {| onSuccess: () => void, onFailure: () => void |}) => void,
  onProductDescriptionChange: (string) => void,
  onProductTitleChange: (string) => void,
  onSnippetImageChange: (string) => void,
  onSnippetImageCropChange: (Crop) => void,
  onTitleChange: (string) => void,
  onTmCarouselDescriptionChange: (string) => void,
  onTmLinkButtonChange: (string) => void,
  onTmUrlChange: (string) => void,
  onUrlChange: (string) => void,
  onVkLinkButtonChange: (string) => void,
  onVkLinkTitleChange: (string) => void,
  onVkMultiDescriptionChange: (string) => void,
  onVkUrlChange: (string) => void,
  onVkWithoutButtonDescriptionChange: (string) => void,
  selectedAudienceIds: Array<number>,
  urlCheck: ?UrlCheckResult,
  vkOptionsWithoutPublic: boolean,
  fbPromotionPosts: Array<OptionType>
|}

export default class EditForm extends Component<Props, State> {
  state = { areChangesPersisted: false }

  render() {
    const data = this.props.data
    return (
      <Fragment>
        <FloatPanel>{this.renderFloatPanelContent()}</FloatPanel>
        {this.props.isAnyErrors && <FormError>{t.form_has_errors}</FormError>}
        <Wrapper className="t-AdvertisementTemplateEdit">
          <ErrorText errors={data.advertisementTemplateErrors.globalErrors} />
          <Row className="t-Name">
            <Header>{t.name}</Header>
            <LineInput value={data.common.name} onChange={this.props.onNameChange} />
            <ErrorText errors={data.advertisementTemplateErrors.nameErrors} />
          </Row>
          {!this.props.hideAudiences && (
            <Row>
              <AudiencesSelectorContainer />
              {this.renderRemovedAudiences()}
            </Row>
          )}
          {this.renderCommonFields()}
          {this.renderMedia()}
          {this.renderAddMediaButton()}
          {this.renderLogoUploader()}
          {this.renderSnippetUploader()}
        </Wrapper>
        <PreviewsContainer
          header={<PreviewHeader>{t.preview}</PreviewHeader>}
          audienceIds={this.props.selectedAudienceIds}
        />
      </Fragment>
    )
  }

  renderCommonFields() {
    const visibility = this.props.controlVisibility
    const configs = this.props.configs
    const data = this.props.data

    return (
      <div>
        <AdInputs
          allowCutView={false}
          columnDescription={data.common.columnDescription}
          columnDescriptionErrors={data.commonErrors.columnDescriptionErrors}
          configs={configs}
          fbMultiDescription={data.common.fbMultiDescription}
          fbMultiDescriptionErrors={data.commonErrors.fbMultiDescriptionErrors}
          fbPromotionPosts={this.props.fbPromotionPosts}
          fbTitle={data.common.fbTitle}
          fbTitleErrors={data.commonErrors.fbTitleErrors}
          fbUrl={data.common.fbUrl}
          fbUrlErrors={data.commonErrors.fbUrlErrors}
          isFbUrlDisabled={false}
          isTmUrlDisabled={false}
          isVkUrlDisabled={false}
          multiDescription={data.common.multiDescription}
          multiDescriptionErrors={data.commonErrors.multiDescriptionErrors}
          onColumnDescriptionChange={this.props.onColumnDescriptionChange}
          onFbMultiDescriptionChange={this.props.onFbMultiDescriptionChange}
          onFbTitleChange={this.props.onFbTitleChange}
          onFbUrlChange={this.props.onFbUrlChange}
          onMultiDescriptionChange={this.props.onMultiDescriptionChange}
          onProductDescriptionChange={this.props.onProductDescriptionChange}
          onProductTitleChange={this.props.onProductTitleChange}
          onTitleChange={this.props.onTitleChange}
          onTmCarouselDescriptionChange={this.props.onTmCarouselDescriptionChange}
          onTmUrlChange={this.props.onTmUrlChange}
          onUrlChange={this.props.onUrlChange}
          onVkLinkTitleChange={this.props.onVkLinkTitleChange}
          onVkMultiDescriptionChange={this.props.onVkMultiDescriptionChange}
          onVkTitleChange={this.props.onVkTitleChange}
          onVkUrlChange={this.props.onVkUrlChange}
          onVkWithoutButtonDescriptionChange={this.props.onVkWithoutButtonDescriptionChange}
          productDescription={data.common.productDescription}
          productDescriptionErrors={data.commonErrors.productDescriptionErrors}
          productTitle={data.common.productTitle}
          productTitleErrors={data.commonErrors.productTitleErrors}
          showColumnDescription={visibility.columnDescription}
          showFbMultiDescription={visibility.fbMultiDescription}
          showFbTitle={visibility.fbTitle}
          showFbUrl={visibility.fbUrl}
          showMultiDescription={visibility.multiDescription}
          showProductDescription={visibility.productDescription}
          showProductTitle={visibility.productTitle}
          showTitle={visibility.title}
          showTmCarouselDescription={visibility.tmCarouselDescription}
          showTmUrl={visibility.tmUrl}
          showUrl={visibility.url}
          showVkLinkTitle={visibility.vkLinkTitle}
          showVkMultiDescription={visibility.vkMultiDescription}
          showVkTitle={visibility.vkTitle}
          showVkUrl={visibility.vkUrl}
          showVkWithoutButtonDescription={visibility.vkWithoutButtonDescription}
          title={data.common.title}
          titleErrors={data.commonErrors.titleErrors}
          tmCarouselDescription={data.common.tmCarouselDescription}
          tmCarouselDescriptionErrors={data.commonErrors.tmCarouselDescriptionErrors}
          tmUrl={data.common.tmUrl}
          tmUrlErrors={data.commonErrors.tmUrlErrors}
          url={data.common.url}
          urlCompact={false}
          urlErrors={data.commonErrors.urlErrors}
          vkLinkTitle={data.common.vkLinkTitle}
          vkLinkTitleErrors={data.commonErrors.vkLinkTitleErrors}
          vkMultiDescription={data.common.vkMultiDescription}
          vkMultiDescriptionErrors={data.commonErrors.vkMultiDescriptionErrors}
          vkTitle={data.common.vkTitle}
          vkTitleErrors={data.commonErrors.vkTitleErrors}
          vkUrl={data.common.vkUrl}
          vkUrlErrors={data.commonErrors.vkUrlErrors}
          vkWithoutButtonDescription={data.common.vkWithoutButtonDescription}
          vkWithoutButtonDescriptionErrors={data.commonErrors.vkWithoutButtonDescriptionErrors}
        />
        {visibility.carousel && data.common.carouselStoreId && (
          <CarouselContainer
            storeId={data.common.carouselStoreId}
            configs={this.props.configs}
            controlVisibility={this.props.controlVisibility}
          />
        )}
        <LinkButtonsContainer
          url={data.common.url}
          urlCheck={this.props.urlCheck}
          configs={configs}
          vkValue={data.common.vkLinkButton}
          tmValue={data.common.tmLinkButton}
          fbValue={data.common.fbLinkButton}
          onVkChange={this.props.onVkLinkButtonChange}
          onTmChange={this.props.onTmLinkButtonChange}
          onFbChange={this.props.onFbLinkButtonChange}
          hideVk={!visibility.vkLinkButton}
          hideTm={!visibility.tmLinkButton}
          hideFb={!visibility.fbLinkButton}
          vkErrors={data.commonErrors.vkLinkButtonErrors}
          tmErrors={data.commonErrors.tmLinkButtonErrors}
          fbErrors={data.commonErrors.fbLinkButtonErrors}
          vkOptionsWithoutPublic={this.props.vkOptionsWithoutPublic}
        />
      </div>
    )
  }

  renderMedia(): any {
    const { media } = this.props.data

    return _.map(media, (singleMedia, index: number) => {
      return (
        <ReplicatedDataContainer
          key={index}
          action={this.props.action}
          configs={this.props.configs}
          controlVisibility={this.props.controlVisibility}
          allowMultiUpload={this.props.allowMultiUpload}
          replicaIndex={index}
        />
      )
    })
  }

  renderLogoUploader() {
    const { data, configs, controlVisibility } = this.props
    const { common, commonErrors } = data

    return (
      <div>
        {controlVisibility.logoImage && (
          <Media>
            {!common.logoImageStoreId && (
              <PaddedWrapper className="t-LogoImage">
                <AdditionalUploaderText>{t.logo_uploader_text}</AdditionalUploaderText>
                <AdditionalImageLoader
                  onLoad={this.props.onLogoImageChange}
                  titleParams={{ title: t.choose_logo_image, fontSize: 16 }}
                  storeId={common.logoImageStoreId}
                  errors={commonErrors.logoImageErrors}
                  config={configs.logoImageConfigs}
                  height={150}
                />
              </PaddedWrapper>
            )}
            {common.logoImageStoreId && (
              <div>
                <PreviewImageCropperContainer
                  title={t.logo_crop}
                  imageStoreId={common.logoImageStoreId}
                  config={configs.logoImageConfigs}
                  crop={common.logoImageCrop}
                  onChange={this.props.onLogoImageCropChange}
                />
                <div className="t-LogoImage">
                  <AdditionalImageLoader
                    onLoad={this.props.onLogoImageChange}
                    storeId={common.logoImageStoreId}
                    errors={commonErrors.logoImageErrors}
                    config={configs.logoImageConfigs}
                    height={150}
                  />
                </div>
              </div>
            )}
          </Media>
        )}
      </div>
    )
  }

  renderSnippetUploader() {
    const { data, configs, controlVisibility } = this.props
    const { common, commonErrors } = data

    return (
      <div>
        {controlVisibility.snippetImage && (
          <Media>
            {!common.snippetImageStoreId && (
              <PaddedWrapper className="t-SnippetImage">
                <AdditionalUploaderText>{t.snippet_uploader_text}</AdditionalUploaderText>
                <AdditionalImageLoader
                  onLoad={this.props.onSnippetImageChange}
                  titleParams={{ title: t.choose_snippet_image, fontSize: 16 }}
                  storeId={common.snippetImageStoreId}
                  errors={commonErrors.snippetImageErrors}
                  config={configs.imageConfigCollection.promo.vk}
                  height={150}
                />
              </PaddedWrapper>
            )}
            {common.snippetImageStoreId && (
              <div>
                <PreviewImageCropperContainer
                  title={t.snippet_crop}
                  imageStoreId={common.snippetImageStoreId}
                  config={configs.imageConfigCollection.promo.vk}
                  crop={common.snippetImageCrop}
                  onChange={this.props.onSnippetImageCropChange}
                />
                <div className="t-SnippetImage">
                  <AdditionalImageLoader
                    onLoad={this.props.onSnippetImageChange}
                    storeId={common.snippetImageStoreId}
                    errors={commonErrors.snippetImageErrors}
                    config={configs.imageConfigCollection.promo.vk}
                    height={150}
                  />
                </div>
              </div>
            )}
          </Media>
        )}
      </div>
    )
  }

  renderRemovedAudiences() {
    return (
      <RemovedAudiencesWrapper>
        {_.map(
          _.difference(this.props.initialSelectedAudienceIds, this.props.selectedAudienceIds),
          (audienceId) => {
            const audience = _.find(
              this.props.data.audiences,
              (a: AdvertisementTemplateAudienceData) => a.id === audienceId
            )
            return (
              <RemovedAudienceComment key={audienceId}>
                {t.ad_in_audience}{' '}
                <RemovedAudienceName>
                  {audience ? `[#${audience.id}] ${audience.name}` : '?'}
                </RemovedAudienceName>{' '}
                {t.will_be_detached}.{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    this.selectAudience(audienceId)
                  }}
                >
                  {t.cancel}
                </a>
              </RemovedAudienceComment>
            )
          }
        )}
      </RemovedAudiencesWrapper>
    )
  }

  selectAudience(id: number) {
    this.props.onAudienceIdsChange(_.concat(this.props.selectedAudienceIds, id))
  }

  renderFloatPanelContent() {
    return (
      <ButtonsWrapper className="container">
        {this.renderPersistButton(t[this.props.action], 'active')}
        {this.props.action !== 'update' && this.renderPersistButton(t.submit_inactive, 'inactive')}
      </ButtonsWrapper>
    )
  }

  renderPersistButton(text: string, status: string) {
    return (
      <StyledButton
        height={32}
        disabled={this.props.isLoading || this.state.areChangesPersisted}
        onClick={() => {
          this.props.onPersist(status, {
            onSuccess: () => {
              this.setState({ areChangesPersisted: true })
              window.location = '/advertisement_templates'
            },
            onFailure: () => {
              scrollToTop()
            }
          })
        }}
      >
        {text}
      </StyledButton>
    )
  }

  renderAddMediaButton() {
    const { action, selectedAudienceIds, controlVisibility } = this.props
    const { columnImage, multiImage, video } = controlVisibility

    if (!_.some([columnImage, multiImage, video])) return null

    return selectedAudienceIds.length > 0 && action === 'create' ? (
      <PaddedWrapper>
        <Button height={32} onClick={() => this.props.onMediaAdd()}>
          {t.add_media}
        </Button>
      </PaddedWrapper>
    ) : null
  }
}
